/**
 * U-02: sizing and number formatting shared by the Reports charts.
 *
 * Recharts lays an axis out in a fixed pixel box and never measures its own
 * tick text, so every width here is ESTIMATED from the characters a label
 * holds (12px system sans averages ~7px a glyph). The estimate only has to be
 * good enough to stop a value label running off the plot or a long insurer
 * name wrapping over its neighbour; the full name always survives in the
 * tooltip and the tick's <title>.
 */

/** Average advance of one glyph at the chart's 11-12px tick size. */
export const CHART_CHAR_PX = 7;

/** Rough rendered width of `text` at the chart tick size, in pixels. */
export function estimateTextWidth(text: string, charPx: number = CHART_CHAR_PX): number {
  return Math.ceil(text.length * charPx);
}

/** `text` cut to `maxChars`, ending in an ellipsis when it was cut. */
export function truncateLabel(text: string, maxChars: number): string {
  if (maxChars < 2 || text.length <= maxChars) return text;
  return `${text.slice(0, maxChars - 1).trimEnd()}…`;
}

/**
 * Indian-system compact number: 2,50,00,000 -> "2.5Cr", 2,00,000 -> "2L",
 * 20,000 -> "20K". Axis ticks only -- the tooltip and bar labels keep the
 * exact figure from the chart's `valueFormatter`.
 */
export function compactIndianNumber(value: number): string {
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  const trim = (n: number) => String(Number(n.toFixed(1)));
  if (abs >= 1e7) return `${sign}${trim(abs / 1e7)}Cr`;
  if (abs >= 1e5) return `${sign}${trim(abs / 1e5)}L`;
  if (abs >= 1e3) return `${sign}${trim(abs / 1e3)}K`;
  return `${sign}${trim(abs)}`;
}

/**
 * The axis tick formatter a chart's `valueFormatter` implies. A money
 * formatter (`formatCurrency` -> "₹1,234") yields compact money ticks
 * ("₹20K"); anything else ("12 policies") yields compact bare numbers, since
 * repeating the unit on every tick is noise the card title already carries.
 */
export function deriveAxisFormatter(
  valueFormatter: (value: number) => string
): (value: number) => string {
  const sample = valueFormatter(1234).trim();
  // A leading run of non-digit, non-letter, non-sign characters is a currency
  // symbol (₹, $, €...).
  const symbol = sample.match(/^[^\d\p{L}\s+-]+/u)?.[0] ?? '';
  return (value: number) => `${symbol}${compactIndianNumber(value)}`;
}
